import React, { useState } from "react";
import { Container, Row, Col, Card, Badge, Nav, Tab } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaHotel, FaCheckCircle, FaLaptopCode, FaBookReader, 
  FaChalkboardTeacher, FaBasketballBall, FaBus, FaMedkit 
} from "react-icons/fa";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";

// Asset Imports
import universityImage from "../assets/ibaaa.jpg";

const LifeAtCampus = () => {
  const [activeTab, setActiveTab] = useState("academic");

  return (
    <div className="life-portal">
      <Header />

      {/* Hero Section: Glassmorphism Design */}
      <section className="life-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={7}>
              <motion.div 
                initial={{ opacity: 0, x: -30 }} 
                animate={{ opacity: 1, x: 0 }}
                className="hero-glass-card"
              >
                <Badge bg="warning" text="dark" className="mb-3 px-3 py-2 fw-bold">STUDENT CENTRIC</Badge>
                <h1 className="display-4 fw-900 text-white mb-4">
                  Life at <span className="text-warning">Sukkur IBA</span>
                </h1>
                <p className="lead text-white-50">
                  Excellence meets opportunity. We provide a dynamic environment fostering 
                  innovation, creativity, and personal growth through state-of-the-art 
                  infrastructure and a vibrant community.
                </p>
              </motion.div>
            </Col>
            <Col lg={5} className="d-none d-lg-block">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }}
                className="hero-image-wrapper"
              >
                <img src={universityImage} alt="Campus" className="img-fluid rounded-4 shadow-2xl" />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content: Tabbed Facility Navigator */}
      <Container className="py-5 mt-n5">
        <Card className="border-0 shadow-lg portal-main-card">
          <Card.Body className="p-0">
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Row className="g-0">
                <Col lg={3} className="bg-light border-end p-4">
                  <h5 className="fw-800 text-navy mb-4 ps-2">Explore Life</h5>
                  <Nav variant="pills" className="flex-column custom-nav">
                    <Nav.Item>
                      <Nav.Link eventKey="academic" className="d-flex align-items-center">
                        <FaChalkboardTeacher className="me-2" /> Academic
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="hostel" className="d-flex align-items-center">
                        <FaHotel className="me-2" /> Hostel Life
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="amenities" className="d-flex align-items-center">
                        <FaBasketballBall className="me-2" /> Amenities
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>

                <Col lg={9} className="p-4 p-md-5">
                  <AnimatePresence mode="wait">
                    <Tab.Content>
                      {/* ACADEMIC TAB */}
                      <Tab.Pane eventKey="academic">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <h3 className="fw-900 text-navy mb-4">World-Class Learning</h3>
                          <Row className="g-4">
                            {[
                              { title: "Smart Classrooms", icon: <FaChalkboardTeacher />, text: "Equipped with latest multimedia technology for interactive learning." },
                              { title: "Digital Library", icon: <FaBookReader />, text: "Quiet study zones and access to millions of global digital resources." },
                              { title: "Advanced Labs", icon: <FaLaptopCode />, text: "High-performance computing environments and specialized tech labs." }
                            ].map((item, i) => (
                              <Col md={4} key={i}>
                                <Card className="h-100 border-0 bg-light facility-card">
                                  <Card.Body className="text-center">
                                    <div className="icon-box-small mb-3">{item.icon}</div>
                                    <h6 className="fw-800">{item.title}</h6>
                                    <p className="small text-muted mb-0">{item.text}</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </motion.div>
                      </Tab.Pane>

                      {/* HOSTEL TAB */}
                      <Tab.Pane eventKey="hostel">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <h3 className="fw-900 text-navy mb-4">Your Home Away From Home</h3>
                          <Row>
                            <Col md={12}>
                              <div className="feature-grid">
                                {[
                                  "Spacious and well-furnished rooms", "24/7 security surveillance", 
                                  "High-speed Wi-Fi connectivity", "Common areas for recreation",
                                  "Mess facilities (Nutritious Meals)", "In-house Laundry services"
                                ].map((feature, i) => (
                                  <div key={i} className="feature-item">
                                    <FaCheckCircle className="text-success me-3" />
                                    <span className="fw-600">{feature}</span>
                                  </div>
                                ))}
                              </div>
                            </Col>
                          </Row>
                        </motion.div>
                      </Tab.Pane>

                      {/* AMENITIES TAB */}
                      <Tab.Pane eventKey="amenities">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <h3 className="fw-900 text-navy mb-4">Campus Amenities</h3>
                          <Row className="g-3">
                            {[
                              { icon: <FaBus />, label: "Transport Services" },
                              { icon: <FaMedkit />, label: "Health & Wellness Center" },
                              { icon: <FaBasketballBall />, label: "Sports Complex" },
                              { icon: <FaHotel />, label: "Student Clubs" }
                            ].map((item, i) => (
                              <Col md={6} key={i}>
                                <div className="amenity-strip">
                                  <div className="strip-icon">{item.icon}</div>
                                  <span className="fw-700">{item.label}</span>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </motion.div>
                      </Tab.Pane>
                    </Tab.Content>
                  </AnimatePresence>
                </Col>
              </Row>
            </Tab.Container>
          </Card.Body>
        </Card>
      </Container>

      <Footer />

      <style>{`
        .life-portal { background-color: #f0f2f5; min-height: 100vh; }
        .text-navy { color: #002B5A; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .fw-600 { font-weight: 600; }

        .life-hero {
          background: linear-gradient(rgba(0, 43, 90, 0.85), rgba(0, 43, 90, 0.95)), 
                      url(${universityImage});
          background-size: cover;
          background-position: center;
          padding: 100px 0 150px 0;
        }

        .hero-glass-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 40px;
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .mt-n5 { margin-top: -80px !important; }

        .portal-main-card {
          border-radius: 24px !important;
          overflow: hidden;
        }

        .custom-nav .nav-link {
          color: #64748b;
          font-weight: 700;
          padding: 12px 20px;
          border-radius: 12px;
          margin-bottom: 8px;
          transition: 0.3s;
        }

        .custom-nav .nav-link.active {
          background-color: #002B5A !important;
          color: white !important;
          box-shadow: 0 4px 12px rgba(0, 43, 90, 0.2);
        }

        .icon-box-small {
          width: 50px; height: 50px;
          background: #e7efff;
          color: #0055A4;
          display: flex; align-items: center; justify-content: center;
          border-radius: 12px; margin: 0 auto;
          font-size: 1.5rem;
        }

        .facility-card {
          transition: 0.3s ease;
          border: 1px solid transparent !important;
        }

        .facility-card:hover {
          transform: translateY(-5px);
          background: #fff !important;
          border-color: #0055A4 !important;
          box-shadow: 0 10px 20px rgba(0,0,0,0.05);
        }

        .feature-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px;
        }

        .feature-item {
          background: #f8fafc;
          padding: 15px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          transition: 0.2s;
        }

        .feature-item:hover {
          background: #fff;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }

        .amenity-strip {
          background: #f8fafc;
          padding: 20px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          border: 1px solid #e2e8f0;
        }

        .strip-icon {
          width: 40px; height: 40px;
          background: #002B5A;
          color: white;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          margin-right: 15px;
        }

        @media (max-width: 991px) {
          .mt-n5 { margin-top: -40px !important; }
          .custom-nav { flex-direction: row !important; overflow-x: auto; white-space: nowrap; }
          .custom-nav .nav-item { margin-right: 10px; }
        }
      `}</style>
    </div>
  );
};

export default LifeAtCampus;